/**
 * API Bridge - Adapts new frontend API calls to legacy backend
 *
 * This is a TEMPORARY adapter layer that allows the new frontend to work
 * with the current/legacy API while backend updates are in progress.
 *
 * TODO: Delete this entire directory once backend is updated
 */

import { legacyClient } from "./legacy-client";
import { transformToLegacy, transformFromLegacy } from "./transformers";
import {
  getTrafficTrends,
  getTrafficSummary,
  getTrafficRealtime,
  getTrafficBreakdown,
  getEntryExitPages,
  getPagesBreakdown,
  getClicksBreakdown,
  getFormsBreakdown,
  // Aggregate endpoints for multi-site mode
  getAggregateTrafficTrends,
  getAggregateTrafficSummary,
  getAggregateTrafficBreakdown,
  getAggregateTrafficRealtime,
  getAggregateEntryExitPages,
  getAggregateClicksBreakdown,
  getAggregateFormsBreakdown,
} from "@/lib/api/external-api";
import type { Property, DataSource } from "@/lib/types/property";
import type {
  TrafficMetrics,
  EventMetrics,
  RealtimeMetrics,
} from "@/lib/types/metrics";
import type { FilterClause } from "@/lib/routing/types";

/**
 * Get property by ID
 */
export async function getProperty(
  propertyId: string,
  token: string
): Promise<Property> {
  // Call legacy API
  const legacyResponse = await legacyClient.getAllProperties(token);

  // Find the property by ID
  const legacyProperty = legacyResponse.properties?.find(
    (p: any) => p.propertyId === propertyId
  );

  if (!legacyProperty) {
    throw new Error("Property not found");
  }

  // Transform to new format
  return transformFromLegacy.property(legacyProperty);
}

/**
 * Get all properties for the user
 */
export async function getPropertyList(token: string): Promise<Property[]> {
  const legacyResponse = await legacyClient.getAllProperties(token);

  return (legacyResponse.properties || []).map((p: any) =>
    transformFromLegacy.property(p)
  );
}

/**
 * Get traffic metrics with filters
 */
export async function getTrafficMetrics(
  propertyId: string,
  period: string,
  filters: FilterClause[],
  sources: string[],
  token: string
): Promise<any> {
  const legacyPeriod = transformToLegacy.period(period);
  const filterParams = filterClausesToParams(filters);

  const [trendsResult, summaryResult, breakdownResult, entryExitResult] =
    await Promise.allSettled([
      getTrafficTrends(legacyPeriod, propertyId, token, filterParams),
      getTrafficSummary(legacyPeriod, propertyId, token, filterParams),
      getTrafficBreakdown(legacyPeriod, propertyId, token, 100, filterParams),
      getEntryExitPages(legacyPeriod, propertyId, token, 100, filterParams),
    ]);

  // Unwrap settled results — null for rejected promises
  const trendsResponse = trendsResult.status === "fulfilled" ? trendsResult.value : null;
  const summaryResponse = summaryResult.status === "fulfilled" ? summaryResult.value : null;
  const breakdownResponse = breakdownResult.status === "fulfilled" ? breakdownResult.value : null;
  const entryExitResponse = entryExitResult.status === "fulfilled" ? entryExitResult.value : null;

  // Log individual failures for debugging (non-fatal)
  if (trendsResult.status === "rejected") console.warn("Traffic trends failed:", trendsResult.reason);
  if (summaryResult.status === "rejected") console.warn("Traffic summary failed:", summaryResult.reason);
  if (breakdownResult.status === "rejected") console.warn("Traffic breakdown failed:", breakdownResult.reason);
  if (entryExitResult.status === "rejected") console.warn("Entry/exit pages failed:", entryExitResult.reason);

  // If every single call failed, throw so the caller knows there's no data at all
  if (!trendsResponse && !summaryResponse && !breakdownResponse && !entryExitResponse) {
    throw new Error("All traffic metric endpoints failed");
  }

  // Revenue is fetched separately by the caller to avoid blocking initial render
  const revenueData: any[] = [];
  const revenueAllData: any[] = [];
  const revenueAttributionData: any[] = [];
  const freeTrialAttributionData: any[] = [];

  // Safe accessors for nullable responses
  const breakdownData = breakdownResponse?.data || {};
  const trendsData = trendsResponse?.data || [];
  const summaryData: any = summaryResponse?.data || {};
  const entryExitData = entryExitResponse?.data || {};

  // Transform breakdown data into old aggregate format
  // Components expect: aggregate: [{ client_os: "Windows", client_os_aggregate: "client_os", count: 123 }, ...]
  const aggregate: any[] = [];

  // Add OS breakdown
  breakdownData.os?.forEach((row: any) => {
    aggregate.push({
      client_os: row.os,
      client_os_aggregate: "client_os",
      count: row.sessions,
    });
  });

  // Add browser breakdown
  breakdownData.browsers?.forEach((row: any) => {
    aggregate.push({
      client_browser: row.browser,
      client_browser_aggregate: "client_browser",
      count: row.sessions,
    });
  });

  // Add device type breakdown
  breakdownData.devices?.forEach((row: any) => {
    aggregate.push({
      device_type: row.device,
      count: row.sessions,
    });
  });

  // Add country breakdown
  breakdownData.countries?.forEach((row: any) => {
    aggregate.push({
      country: row.country,
      count: row.sessions,
    });
  });

  // Add city breakdown
  breakdownData.cities?.forEach((row: any) => {
    aggregate.push({
      city: row.city,
      country: row.country,
      count: row.sessions,
    });
  });

  // Add region breakdown
  breakdownData.regions?.forEach((row: any) => {
    aggregate.push({
      region: row.region,
      country: row.country,
      count: row.sessions,
    });
  });

  const isKnownUtmValue = (v: any) => v && v !== 'unknown'

  // Add UTM breakdown with referer_group for channels.
  // Skip rows where source/medium are "unknown" — those are placeholder entries
  // from the API for sessions without real UTM params.
  breakdownData.utm?.forEach((row: any) => {
    if (!isKnownUtmValue(row.source) && !isKnownUtmValue(row.medium)) return
    aggregate.push({
      utm_source: isKnownUtmValue(row.source) ? row.source : undefined,
      utm_medium: isKnownUtmValue(row.medium) ? row.medium : undefined,
      utm_campaign: isKnownUtmValue(row.campaign) ? row.campaign : undefined,
      referer_group: isKnownUtmValue(row.medium) ? row.medium : "referral",
      referer_group_aggregate: "referer_group",
      count: row.sessions,
    });
  });

  // Add referrer breakdown — store the actual domain as referer_group so the
  // Referrals tab shows real domains. Channel categorisation happens in the
  // component via categorizeChannel().
  breakdownData.referrers?.forEach((row: any) => {
    aggregate.push({
      referer_group: row.referrer || null,
      referer_group_aggregate: "referer_group",
      count: row.sessions,
    });
  });

  // Build utmResults array for ReferralComponent
  // ReferralComponent expects separate entries with aggregate flags for each dimension
  const utmResults: any[] = [];

  // Add UTM Source aggregates
  breakdownData.utm?.forEach((row: any) => {
    if (isKnownUtmValue(row.source)) {
      utmResults.push({
        utm_source: row.source,
        utm_source_aggregate: 'utm_source',
        count: row.sessions.toString(),
      });
    }
  });

  // Add UTM Medium aggregates
  breakdownData.utm?.forEach((row: any) => {
    if (isKnownUtmValue(row.medium)) {
      utmResults.push({
        utm_medium: row.medium,
        utm_medium_aggregate: 'utm_medium',
        count: row.sessions.toString(),
      });
    }
  });

  // Add UTM Campaign aggregates
  breakdownData.utm?.forEach((row: any) => {
    if (isKnownUtmValue(row.campaign)) {
      utmResults.push({
        utm_campaign: row.campaign,
        utm_campaign_aggregate: 'utm_campaign',
        count: row.sessions.toString(),
      });
    }
  });
  
  // Add Referrer data for "Referral" tab
  breakdownData.referrers?.forEach((row: any) => {
    utmResults.push({
      referer_group: row.referrer,
      count: row.sessions.toString(),
    });
  });

  // Transform entry/exit pages (safe for null entryExitResponse)
  const transformedEntryPages = (entryExitData.entry_pages || []).map(
    (page: any) => ({
      page: page.page,
      entry_count: page.count,
    })
  );

  const transformedExitPages = (entryExitData.exit_pages || []).map(
    (page: any) => ({
      page: page.page,
      entry_count: page.count,
    })
  );

  const result = {
    metrics: {
      // Trend data for charts - components expect these as separate arrays
      trends: {
        // Page views trend
        views: (trendsData as any[]).map((bucket: any) => ({
          bucket: bucket.bucket_start,
          count: bucket.page_views,
        })),
        // Sessions/visitors trend
        sessions: (trendsData as any[]).map((bucket: any) => ({
          bucket: bucket.bucket_start,
          visitors: bucket.unique_visitors,
        })),
        // Total sessions summary (used for bounce rate calculation)
        totalSessions: [
          {
            total_sessions: summaryData.total_sessions ?? 0,
          },
        ],
        // Bounce data
        bounces: [
          {
            bounce_count: summaryData.bounce_count ?? 0,
          },
        ],
        // Single page sessions (for regular bounce rate)
        singlePageSessions: [
          {
            bounce_count: summaryData.single_page_sessions ?? 0,
          },
        ],
        // Session time
        sessionTime: [
          {
            total_session_time: summaryData.total_session_time_ms ?? 0,
          },
        ],
        // Revenue data — populated lazily via a separate fetch; starts empty
        revenue: revenueData,
        revenueAll: revenueAllData,
        revenueAttribution: revenueAttributionData,
        freeTrials: [],
        freeTrialAttribution: freeTrialAttributionData,
      },
      subscriptions: null,
      // Aggregate array with devices, locations, referrers, etc.
      aggregate: aggregate,
      // UTM Results for ReferralComponent
      utmResults: utmResults,
      // Entry pages
      entryPages: transformedEntryPages,
      // Exit pages
      exitPages: transformedExitPages,
      // Pages data (for "Pages" tab in PagesComponent)
      pages: {
        pageSessions: (breakdownData.landing_pages || []).map(
          (page: any) => ({
            page: page.path,
            count: page.sessions?.toString() ?? "0",
          })
        ),
        pageViews: (breakdownData.landing_pages || []).map(
          (page: any) => ({
            page: page.path,
            count: page.page_views?.toString() ?? "0",
          })
        ),
      },
      // Top level for backward compatibility
      pageViews: summaryData.page_views ?? 0,
      uniqueVisitors: summaryData.unique_visitors ?? 0,
      totalSessions: summaryData.total_sessions ?? 0,
      bounceRate: summaryData.bounce_rate ?? 0,
      avgSessionDuration: summaryData.avg_session_duration ?? 0,
    },
  };

  return result;
}

/**
 * Get event metrics (pages, clicks, forms)
 */
/**
 * Convert FilterClause[] to flat Record<string, string> for external API query params.
 * Only equality filters are supported. Field names map directly to query param names.
 */
export function filterClausesToParams(filters: FilterClause[]): Record<string, string> {
  const params: Record<string, string> = {};
  // Map FilterClause field to external API query param name
  const fieldMap: Record<string, string> = {
    page: "page",
    entry_page: "entry_page",
    exit_page: "exit_page",
    country: "country",
    region: "region",
    browser: "browser",
    os: "os",
    device: "device",
    device_type: "device_type",
    referrer: "referrer",
    utm_source: "utm_source",
    utm_campaign: "utm_campaign",
    utm_medium: "utm_medium",
    utm_content: "utm_content",
    utm_id: "utm_id",
    utm_term: "utm_term",
  };

  for (const filter of filters) {
    const paramName = fieldMap[filter.field];
    if (!paramName) continue;

    if (filter.operator === ":") {
      params[paramName] = filter.value;
    } else if (filter.operator === "!=") {
      params[`exclude_${paramName}`] = filter.value;
    }
  }
  return params;
}

export async function getEventMetrics(
  propertyId: string,
  period: string,
  filters: FilterClause[],
  sources: string[],
  view: "pages" | "clicks" | "forms",
  token: string
): Promise<any> {
  const legacyPeriod = transformToLegacy.period(period);
  const filterParams = filterClausesToParams(filters);

  let response;

  switch (view) {
    case "pages":
      response = await getPagesBreakdown(legacyPeriod, propertyId, token, 100, filterParams);
      break;
    case "clicks":
      response = await getClicksBreakdown(legacyPeriod, propertyId, token, 100, filterParams);
      break;
    case "forms":
      response = await getFormsBreakdown(legacyPeriod, propertyId, token, 100, undefined, filterParams);
      break;
    default:
      throw new Error(`Unknown view type: ${view}`);
  }

  return response;
}

/**
 * Get realtime metrics
 */
async function getRealtimeMetrics(
  propertyId: string,
  token: string
): Promise<any> {
  return await getTrafficRealtime(propertyId, token);
}

/**
 * Get aggregated traffic metrics across multiple properties
 * Uses backend aggregation endpoints for accurate multi-site data.
 * Always uses aggregate endpoints — pass [propertyId] for single-property mode.
 */
export async function getAggregatedTrafficMetrics(
  propertyIds: string[],
  period: string,
  filters: FilterClause[],
  token: string,
  sharePropertyId?: string
): Promise<any> {
  if (!propertyIds || propertyIds.length === 0) {
    throw new Error("No property IDs provided");
  }

  const legacyPeriod = transformToLegacy.period(period);
  const filterParams = filterClausesToParams(filters);

  const [trendsResponse, summaryResponse, breakdownResponse, entryExitResponse] =
    await Promise.all([
      getAggregateTrafficTrends(propertyIds, legacyPeriod, token, sharePropertyId, filterParams),
      getAggregateTrafficSummary(propertyIds, legacyPeriod, token, sharePropertyId, filterParams),
      getAggregateTrafficBreakdown(propertyIds, legacyPeriod, token, 100, sharePropertyId, filterParams),
      getAggregateEntryExitPages(propertyIds, legacyPeriod, token, 100, sharePropertyId, filterParams),
    ]);

  // Revenue is fetched separately by the caller to avoid blocking initial render
  const combinedRevenue: any[] = [];
  const combinedRevenueAll: any[] = [];
  const combinedRevenueAttribution: any[] = [];

  // Transform aggregated breakdown data into legacy format
  const aggregate: any[] = [];

  // Add OS breakdown
  breakdownResponse.data.os?.forEach((row: any) => {
    aggregate.push({
      client_os: row.os,
      client_os_aggregate: "client_os",
      count: row.sessions,
    });
  });

  // Add browser breakdown
  breakdownResponse.data.browsers?.forEach((row: any) => {
    aggregate.push({
      client_browser: row.browser,
      client_browser_aggregate: "client_browser",
      count: row.sessions,
    });
  });

  // Add device type breakdown
  breakdownResponse.data.devices?.forEach((row: any) => {
    aggregate.push({
      device_type: row.device,
      count: row.sessions,
    });
  });

  // Add country breakdown
  breakdownResponse.data.countries?.forEach((row: any) => {
    aggregate.push({
      country: row.country,
      count: row.sessions,
    });
  });

  // Add city breakdown
  breakdownResponse.data.cities?.forEach((row: any) => {
    aggregate.push({
      city: row.city,
      country: row.country,
      count: row.sessions,
    });
  });

  // Add region breakdown
  breakdownResponse.data.regions?.forEach((row: any) => {
    aggregate.push({
      region: row.region,
      country: row.country,
      count: row.sessions,
    });
  });

  const isKnownUtmValueAgg = (v: any) => v && v !== 'unknown'

  // Add UTM breakdown with referer_group for channels.
  // Skip rows where source/medium are "unknown" — placeholder entries for sessions without real UTM params.
  breakdownResponse.data.utm?.forEach((row: any) => {
    if (!isKnownUtmValueAgg(row.source) && !isKnownUtmValueAgg(row.medium)) return
    aggregate.push({
      utm_source: isKnownUtmValueAgg(row.source) ? row.source : undefined,
      utm_medium: isKnownUtmValueAgg(row.medium) ? row.medium : undefined,
      utm_campaign: isKnownUtmValueAgg(row.campaign) ? row.campaign : undefined,
      referer_group: isKnownUtmValueAgg(row.medium) ? row.medium : "referral",
      referer_group_aggregate: "referer_group",
      count: row.sessions,
    });
  });

  // Add referrer breakdown — store the actual domain as referer_group so the
  // Referrals tab shows real domains. Channel categorisation happens in the
  // component via categorizeChannel().
  breakdownResponse.data.referrers?.forEach((row: any) => {
    aggregate.push({
      referer_group: row.referrer || null,
      referer_group_aggregate: "referer_group",
      count: row.sessions,
    });
  });

  // Build utmResults array for ReferralComponent
  const utmResults: any[] = [];

  breakdownResponse.data.utm?.forEach((row: any) => {
    if (isKnownUtmValueAgg(row.source)) {
      utmResults.push({
        utm_source: row.source,
        utm_source_aggregate: "utm_source",
        count: row.sessions.toString(),
      });
    }
  });

  breakdownResponse.data.utm?.forEach((row: any) => {
    if (isKnownUtmValueAgg(row.medium)) {
      utmResults.push({
        utm_medium: row.medium,
        utm_medium_aggregate: "utm_medium",
        count: row.sessions.toString(),
      });
    }
  });

  breakdownResponse.data.utm?.forEach((row: any) => {
    if (isKnownUtmValueAgg(row.campaign)) {
      utmResults.push({
        utm_campaign: row.campaign,
        utm_campaign_aggregate: "utm_campaign",
        count: row.sessions.toString(),
      });
    }
  });

  breakdownResponse.data.referrers?.forEach((row: any) => {
    utmResults.push({
      referer_group: row.referrer,
      count: row.sessions.toString(),
    });
  });

  // Transform entry/exit pages
  const transformedEntryPages = (entryExitResponse.data?.entry_pages || []).map(
    (page: any) => ({
      page: page.page,
      entry_count: page.count,
    })
  );

  const transformedExitPages = (entryExitResponse.data?.exit_pages || []).map(
    (page: any) => ({
      page: page.page,
      entry_count: page.count,
    })
  );

  // Build result in legacy format
  const result = {
    metrics: {
      trends: {
        views: trendsResponse.data.map((bucket) => ({
          bucket: bucket.bucket_start,
          count: bucket.page_views,
        })),
        sessions: trendsResponse.data.map((bucket) => ({
          bucket: bucket.bucket_start,
          visitors: bucket.unique_visitors,
        })),
        totalSessions: [
          {
            total_sessions: summaryResponse.data.total_sessions,
          },
        ],
        bounces: [
          {
            bounce_count: summaryResponse.data.bounce_count,
          },
        ],
        singlePageSessions: [
          {
            bounce_count: summaryResponse.data.single_page_sessions,
          },
        ],
        sessionTime: [
          {
            total_session_time: summaryResponse.data.total_session_time_ms,
          },
        ],
        revenue: combinedRevenue,
        revenueAll: combinedRevenueAll,
        revenueAttribution: combinedRevenueAttribution,
        freeTrialAttribution: [],
      },
      aggregate: aggregate,
      utmResults: utmResults,
      entryPages: transformedEntryPages,
      exitPages: transformedExitPages,
      pages: {
        pageSessions: (breakdownResponse.data?.landing_pages || []).map(
          (page: any) => ({
            page: page.path,
            count: page.sessions.toString(),
          })
        ),
        pageViews: (breakdownResponse.data?.landing_pages || []).map(
          (page: any) => ({
            page: page.path,
            count: page.page_views.toString(),
          })
        ),
      },
      pageViews: summaryResponse.data.page_views,
      uniqueVisitors: summaryResponse.data.unique_visitors,
      totalSessions: summaryResponse.data.total_sessions,
      bounceRate: summaryResponse.data.bounce_rate,
      avgSessionDuration: summaryResponse.data.avg_session_duration,
    },
    // Add aggregate metadata
    isAggregated: true,
    propertyIds: propertyIds,
    propertyCount: propertyIds.length,
  };

  return result;
}

/**
 * Get aggregated event metrics (clicks, forms) across multiple properties
 * Always uses aggregate endpoints — pass [propertyId] for single-property mode.
 */
export async function getAggregatedEventMetrics(
  propertyIds: string[],
  period: string,
  filters: FilterClause[],
  view: "clicks" | "forms",
  token: string,
  sharePropertyId?: string
): Promise<any> {
  if (!propertyIds || propertyIds.length === 0) {
    throw new Error("No property IDs provided");
  }

  const legacyPeriod = transformToLegacy.period(period);
  const filterParams = filterClausesToParams(filters);

  let response;
  switch (view) {
    case "clicks":
      response = await getAggregateClicksBreakdown(propertyIds, legacyPeriod, token, 50, sharePropertyId, filterParams);
      break;
    case "forms":
      response = await getAggregateFormsBreakdown(propertyIds, legacyPeriod, token, 50, sharePropertyId, filterParams);
      break;
    default:
      throw new Error(`Unknown view type: ${view}`);
  }

  return response;
}

/**
 * Get aggregated realtime metrics across multiple properties
 * Always uses aggregate endpoint — pass [propertyId] for single-property mode.
 */
export async function getAggregatedRealtimeMetrics(
  propertyIds: string[],
  token: string
): Promise<any> {
  if (!propertyIds || propertyIds.length === 0) {
    throw new Error("No property IDs provided");
  }

  return await getAggregateTrafficRealtime(propertyIds, token);
}

/**
 * Get workspace-level metrics from multiple connectors
 * Aggregates data from all connected sources using backend aggregation
 */
async function getWorkspaceMetrics(
  workspaceId: string,
  period: string,
  filters: FilterClause[],
  connectorIds: string[],
  token: string
): Promise<any> {
  // Use the aggregated traffic metrics function
  return await getAggregatedTrafficMetrics(connectorIds, period, filters, token);
}

/**
 * Create new property
 */
export async function createProperty(
  name: string,
  domain: string,
  token: string
): Promise<Property> {
  const legacyResponse = await legacyClient.createProperty(name, domain, token);
  return transformFromLegacy.property(legacyResponse);
}

/**
 * Update property
 */
export async function updateProperty(
  propertyId: string,
  updates: { name?: string },
  token: string
): Promise<Property> {
  // Legacy API might not support updates, handle gracefully
  try {
    const legacyResponse = await legacyClient.updateProperty(
      propertyId,
      updates,
      token
    );
    return transformFromLegacy.property(legacyResponse);
  } catch (error) {
    console.error("Failed to update property via legacy API:", error);
    throw error;
  }
}

/**
 * Delete property
 */
export async function deleteProperty(
  propertyId: string,
  token: string
): Promise<{ success: boolean }> {
  await legacyClient.deleteProperty(propertyId, token);
  return { success: true };
}

// Export legacy client for endpoints that don't need transformation yet
